import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
  Popover,
  PopoverBody,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Helmet from 'react-helmet';
import { PseudoStates, useFeatures } from '@cordata/buffet';
import { parse, format } from 'date-fns';
import { uniq } from 'lodash';

import usePatient from '~/utils/patient/hook';
import { MatchStatus } from '~/utils/const/identity';
import OtherUser from '~/assets/other-user.png';
import DisputeAlertModal from '~/components/Patient/Identity/Dispute/AlertModal';

const PatientsBanner = ({ full, enableDisputeAlertModal }) => {
  const [status, patient] = usePatient();
  const { isIdentityHealthcareUser } = useFeatures();
  const [isDisputeAlertModalOpen, setIsDisputeAlertModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState('');

  const toggleOpenAlert = (alertId) => {
    if (openAlert === alertId) {
      setOpenAlert('');
    } else {
      setOpenAlert(alertId);
    }
  };

  const toggleIsDisputeAlertModalOpen = () => {
    setIsDisputeAlertModalOpen(previous => !previous);
  };

  useEffect(() => {
    if (isIdentityHealthcareUser &&
        enableDisputeAlertModal &&
        patient.matchResults.hasAnyDisputes) {
      setIsDisputeAlertModalOpen(true);
    }
  }, []);

  return (
    <Card className="rounded-0 border-dark border-bottom border-right-0 border-left-0 border-top-0">
      <DisputeAlertModal
        isOpen={isDisputeAlertModalOpen}
        toggleIsOpen={toggleIsDisputeAlertModalOpen}
      />

      {status === 'success' ? (
        <CardBody className="p-2">
          <Helmet
            titleTemplate={`%s - ${patient.lastName}, ${patient.firstName} ( ${patient.sacwisPersonId} ) - Identity / Navigator`}
            defaultTitle={`${patient.lastName}, ${patient.firstName} ( ${patient.sacwisPersonId} ) - Identity / Navigator`}
          />

          <div className="d-flex align-items-top">
            <div className="flex-shrink-0">
              <img src={OtherUser} className="bg-tertiary rounded" alt={patient.gender} width={66} height={66} />
            </div>

            <div className="flex-grow-1 ms-3 ml-3">
              <CardTitle>
                <strong>{patient.lastName}, {patient.firstName} {patient.middleName === 'NaN' ? '' : patient.middleName}</strong>

                <small className="d-block">
                  {patient.preferredNames && patient.preferredNames.length > 0 && (
                  <>
                    {'"'}{uniq(patient.preferredNames.map(pN => `${pN.attributes.preferredname}`)).join(', ')}{'"'}<br />
                  </>)}
                  {format(parse(patient.birthDate), 'MM/DD/YYYY')}{' '}
                  ( {patient.adaptiveAge} )<br />
                  {patient.gender ? `(${patient.gender})` : ''}<br />
                </small>
              </CardTitle>
            </div>
          </div>

          <hr />

          {patient.patientFlags?.length > 0
            ? (
              <>
                {patient.patientFlags.map(pf => (
                  <React.Fragment key={pf.id}>
                    <Badge
                      id={`patient-flag-${pf.id}`}
                      color="secondary"
                      pill
                      className="cursor-pointer"
                      onClick={() =>
                        toggleOpenAlert(`patient-flag-${pf.id}`)
                      }
                    >
                      <FontAwesomeIcon icon="exclamation-circle" className="mr-3" />
                      {pf.patientflag}
                    </Badge>

                    <Popover
                      placement="bottom"
                      target={`patient-flag-${pf.id}`}
                      isOpen={openAlert.includes(pf.id)}
                      toggle={() =>
                        toggleOpenAlert(`patient-flag-${pf.id}`)
                      }
                    >
                      <PopoverBody>
                        <div>
                          {pf.flaginfo}
                        </div>

                        <p>via {pf.sourceDescription} / {pf.county}</p>
                      </PopoverBody>
                    </Popover>
                  </React.Fragment>
                ))}

                <hr />
              </>
            ) : null}

          <div className="p-3">
            <Row noGutters>
              <Col className="font-weight-bold">
                PCSA County
              </Col>

              <Col>
                {patient.agencyCounty}
              </Col>
            </Row>

            <hr />

            <Row noGutters>
              <Col className="font-weight-bold">
                Legal Status
              </Col>

              <Col>
                {patient.csChildLegalStatusLegal}
              </Col>
            </Row>

            <hr />

            <Row noGutters>
              <Col className="font-weight-bold">
                Placement Type
              </Col>

              <Col>
                {patient.placementType}
              </Col>
            </Row>

            <hr />

            <Row noGutters>
              <Col className="font-weight-bold">
                Placement
              </Col>

              <Col>
                {patient.providerName}
              </Col>
            </Row>

            <hr />

            <div className="font-weight-bold">
              Medical Record #
            </div>

            {patient.matchResults.matches.length === 0 ? (
              <div>No matched MRNs</div>
            ) : (
              <>
                {Object.keys(patient.matchResults.matchesBySource)
                  .filter(source => !patient.matchResults.matchesBySource[source].matches
                    .every(m => m.matchStatusId === MatchStatus.Rejected))
                  .sort()
                  .map(source => (
                    <div className="pl-1" key={source}>
                      <div>
                        {source}

                        <div className="pl-1">
                          {patient.matchResults.matchesBySource[source].matches
                            .filter(m => m.matchStatusId === MatchStatus.Approved)
                            .map(m => (
                              <div key={m.lookuppersonid}>{m.lookuppersonid}</div>
                            ))
                          }

                          {patient.matchResults.matchesBySource[source].matches
                            .some(m => m.matchStatusId !== MatchStatus.Approved
                              && m.matchStatusId !== MatchStatus.Rejected) &&
                              <div className="text-danger">Another MRN is under review</div>
                          }
                        </div>
                      </div>
                    </div>
                  ))
                }
              </>
            )}

            <hr />

            <Row noGutters>
              <Col className="font-weight-bold">
                SACWIS Person ID
              </Col>

              <Col>
                {patient.sacwisPersonId}
              </Col>
            </Row>

            {full && (
            <>
              <hr />

              <Row noGutters>
                <Col className="font-weight-bold">
                  SACWIS Case ID
                </Col>

                <Col>
                  {uniq(patient.sourceCases.map(sc => sc.caseId)).join(', ')}
                </Col>
              </Row>

              <hr />

              <Row noGutters>
                <Col className="font-weight-bold">
                  Provider ID
                </Col>

                <Col>
                  {patient.providerId}
                </Col>
              </Row>
            </>)}
          </div>
        </CardBody>
      ) : (
        <PseudoStates status={status} />
      )}
    </Card>
  );
};

PatientsBanner.propTypes = {
  full: PropTypes.bool,
  enableDisputeAlertModal: PropTypes.bool,
};

PatientsBanner.defaultProps = {
  full: false,
  enableDisputeAlertModal: false,
};

export default PatientsBanner;
